import React, { useEffect, useMemo, useState } from 'react';
import { Form, Input, Button, Alert, Tag, Tooltip, Space } from 'antd';
import { MailOutlined, ThunderboltOutlined, InfoCircleOutlined, BankOutlined, UserOutlined, AuditOutlined, CalendarOutlined, ClockCircleOutlined, BellOutlined } from '@ant-design/icons';
import CBModal from '../../components/CBModal';
import axiosInstance from '../../variables/api/axiosInstance';
import { partieGarantie, adresseGarantie } from '../../services/partieGarantie';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import {
  formatDateDocument, langueDocument, langueVictime, tDocument,
} from '../../i18n/documents';
import { formatDate } from '../../variables/constants';

import { libelleDelai } from '../../i18n/donnees';

const GREEN = '#1a6b3c';

/**
 * Modal de relance contextualisée.
 * - Pré-remplit destinataire + sujet + corps en fonction de la catégorie du délai
 * - Détecte automatiquement le contexte (PV, RC, garantie, pièces, etc.)
 * - L'utilisateur valide/édite avant envoi
 */
export default function RelanceModal({ open, onClose, onSent, notification }) {
  // La fenêtre parle la langue de l'agent ; le sujet et le message pré-remplis,
  // celle du destinataire (detecterContexte).
  const { t } = useTranslation('documents');
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [contexte, setContexte] = useState(null);
  const [loadingContext, setLoadingContext] = useState(false);

  /* Reset + détection du contexte à l'ouverture */
  useEffect(() => {
    if (!open || !notification) return;
    setLoadingContext(true);
    detecterContexte(notification, t)
      .then(ctx => {
        setContexte(ctx);
        form.setFieldsValue({
          destinataire: ctx.destinataireSuggere || '',
          sujet: ctx.sujetSuggere,
          corps: ctx.corpsSuggere,
        });
      })
      .finally(() => setLoadingContext(false));
  // t change avec la langue de l'écran : sans lui ici, la fenêtre resterait
  // dans la langue de son premier affichage.
  }, [open, notification, form, t]);

  const onSubmit = async (vals) => {
    if (!notification) return;
    setSubmitting(true);
    try {
      await axiosInstance.post(`/v1/delais/${notification.id}/relancer`, {
        destinataire: vals.destinataire?.trim() || null,
        sujet: vals.sujet?.trim() || null,
        corps: vals.corps || null,
      }, {
        // Le message d'échec est affiché ici, en contexte, et la modale reste
        // ouverte avec le texte saisi. Sans ce drapeau, l'intercepteur global
        // afficherait le même message une seconde fois.
        skipErrorToast: true,
      });
      onSent?.();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('ecran.relance.erreurEnvoi'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!notification) return null;

  const niveauTag = {
    CRITIQUE: { color: 'error', label: t('ecran.relance.niveaux.CRITIQUE') },
    URGENT: { color: 'warning', label: t('ecran.relance.niveaux.URGENT') },
    ATTENTION: { color: 'gold', label: t('ecran.relance.niveaux.ATTENTION') },
    NORMAL: { color: 'success', label: t('ecran.relance.niveaux.NORMAL') },
  }[notification.niveauAlerte] || { color: 'default', label: notification.niveauAlerte };

  return (
    <CBModal
      open={open}
      onCancel={onClose}
      title={
        <Space>
          <ThunderboltOutlined style={{ color: GREEN }} />
          {t('ecran.relance.titre', { numero: notification.sinistreNumeroLocal })}
        </Space>
      }
      footer={null}
      width={720}
      destroyOnClose
    >
      {/* Bandeau contexte du délai */}
      <div style={{
        background: '#f0fdf4', border: `1px solid ${GREEN}`,
        borderRadius: 10, padding: '12px 16px', marginBottom: 16,
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {t('ecran.relance.delaiConcerne')}
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginTop: 2 }}>
              {notification.libelleDelai}
            </div>
            {notification.referenceJuridique && (
              <Tooltip title={t('ecran.relance.articleJustifiant')}>
                <span style={{
                  display: 'inline-block', marginTop: 6,
                  fontSize: 11, fontFamily: 'ui-monospace, monospace',
                  padding: '2px 8px', background: '#fef3c7',
                  border: '1px solid #fde68a', borderRadius: 6,
                  color: '#78350f',
                }}>
                  <AuditOutlined style={{ marginRight: 4 }} />{notification.referenceJuridique}
                </span>
              </Tooltip>
            )}
          </div>
          <Tag color={niveauTag.color} style={{ borderRadius: 12, fontWeight: 600 }}>
            {niveauTag.label}
          </Tag>
        </div>
        <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#374151', flexWrap: 'wrap' }}>
          <span><CalendarOutlined style={{ marginRight: 4 }} />{t('ecran.relance.echeance')} <strong>{formatDate(notification.dateEcheance)}</strong></span>
          <span style={{ color: notification.joursRestants <= 0 ? '#dc2626' : '#374151' }}>
            <ClockCircleOutlined style={{ marginRight: 4 }} />{notification.joursRestants <= 0 ? t('ecran.relance.depasse', { jours: Math.abs(notification.joursRestants) }) : t('ecran.relance.restants', { jours: notification.joursRestants })}
          </span>
          {notification.nombreAlertes > 0 && (
            <span><BellOutlined style={{ marginRight: 4 }} />{t('ecran.relance.dejaEnvoyees', { count: notification.nombreAlertes })}</span>
          )}
        </div>
      </div>

      {/* Indicateurs de contexte détecté */}
      {!loadingContext && contexte && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
          {contexte.destinataireSuggere && (
            <Tag icon={['HOMOLOGUE', 'COMPAGNIE'].includes(contexte.typeDestinataire) ? <BankOutlined /> : <UserOutlined />}
              color="green" style={{ borderRadius: 12, padding: '4px 10px' }}>
              {t('ecran.relance.destinataireSuggere', { cible: contexte.libelleCible })}
            </Tag>
          )}
          {contexte.manquants?.length > 0 && (
            <Tag color="orange" style={{ borderRadius: 12, padding: '4px 10px' }}>
              <InfoCircleOutlined style={{ marginRight: 4 }} />
              {t('ecran.relance.elementsDetectes', { count: contexte.manquants.length })}
            </Tag>
          )}
        </div>
      )}

      {!loadingContext && contexte?.alerteContexte && (
        <Alert
          type="info" showIcon style={{ marginBottom: 14, borderRadius: 8 }}
          message={contexte.alerteContexte}
        />
      )}

      {!loadingContext && contexte?.langueDocument === 'en' && (
        <Alert
          type="info" showIcon style={{ marginBottom: 14, borderRadius: 8 }}
          message={t('ecran.messageEnAnglais')}
        />
      )}

      <Form form={form} layout="vertical" onFinish={onSubmit}>
        <Form.Item name="destinataire" label={t('ecran.relance.destinataire')}
          extra={t('ecran.relance.destinataireAide')}
          rules={[{ type: 'email', message: t('ecran.relance.emailInvalide') }]}>
          <Input placeholder={t('ecran.relance.destinataireExemple')} />
        </Form.Item>

        <Form.Item name="sujet" label={t('ecran.relance.sujet')}
          rules={[{ required: true, message: t('ecran.relance.sujetRequis') }]}>
          <Input placeholder={t('ecran.relance.sujetPlaceholder')} />
        </Form.Item>

        <Form.Item name="corps" label={t('ecran.relance.message')}
          rules={[{ required: true, message: t('ecran.relance.messageRequis') }]}>
          <Input.TextArea rows={10} style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12.5 }} />
        </Form.Item>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, paddingTop: 12, borderTop: `1px solid ${GREEN}` }}>
          <Button onClick={onClose}>{t('ecran.annuler')}</Button>
          <Button type="primary" htmlType="submit" loading={submitting} icon={<MailOutlined />}
            style={{ background: GREEN, borderColor: GREEN }}>
            {t('ecran.relance.envoyer')}
          </Button>
        </div>
      </Form>
    </CBModal>
  );
}

/* ─────────────────────────────────────────────────────────────────────
   Détection automatique du contexte selon la catégorie du délai.
   Récupère les infos manquantes (PV incomplet, pièces victime, RC, etc.)
   pour pré-remplir un mail pertinent.
   Deux langues : `t`, celle de l'agent, pour ce que la fenêtre lui affiche
   (cible, alerte) ; `d`, celle du destinataire, pour le sujet et le message qui
   partent chez lui. Le destinataire est un partenaire (compagnie, bureau
   homologue) pour la garantie et le recours, la victime pour l'offre (langue de
   sa fiche) ; ailleurs, il est au Togo.
   ───────────────────────────────────────────────────────────────────── */
export
async function detecterContexte(n, t) {
  let destinataireSuggere = '';
  let typeDestinataire = 'INTERNE';
  let libelleCible = t('ecran.relance.cibles.redacteur');
  let alerteContexte = null;
  let organismeDestinataire = null;
  let corpsSpecifique = (d, sinistre, date, libelle) => d('relance.corps.defaut', { libelle, sinistre });
  const manquants = [];
  // fonctions (d) => texte : ils partent dans le message

  // Récupération du sinistre + pièces victime selon catégorie
  let sinistreData = null;
  if (n.sinistreTrackingId) {
    try {
      const r = await axiosInstance.get(`/v1/sinistres/${n.sinistreTrackingId}`);
      sinistreData = r.data?.data;
    } catch { /* silencieux */ }
  }

  switch (n.categorie) {
    case 'GARANTIE': {
      // Cible : celui à qui la garantie a été demandée - bureau homologue pour un ET,
      // compagnie togolaise pour un TE. L'adresse du référentiel est proposée : sans
      // elle, la relance partait aux seuls agents internes.
      const partie = partieGarantie(sinistreData);
      if (partie.role === 'COMPAGNIE') {
        libelleCible = t('ecran.relance.cibles.compagnie', { nom: partie.libelle || '-' });
        typeDestinataire = 'COMPAGNIE';
      } else if (n.homologueRaisonSociale || partie.libelle) {
        libelleCible = t('ecran.relance.cibles.homologue', { nom: n.homologueRaisonSociale || partie.libelle }) + (n.homologueCodePays ? ` (${n.homologueCodePays})` : '');
        typeDestinataire = 'HOMOLOGUE';
      }
      organismeDestinataire = partie.organismeTrackingId;
      destinataireSuggere = sinistreData ? await adresseGarantie(sinistreData) : '';
      alerteContexte = partie.role === 'COMPAGNIE'
        ? t('ecran.relance.alertes.garantieCompagnie')
        : t('ecran.relance.alertes.garantieHomologue');
      corpsSpecifique = (d, sinistre) => d('relance.corps.garantie', { sinistre });
      break;
    }
    case 'PV': {
      libelleCible = t('ecran.relance.cibles.provenancePv');
      if (sinistreData && sinistreData.pvComplet === false) {
        manquants.push((d) => d('relance.manquants.pvIncomplet'));
      }
      alerteContexte = t('ecran.relance.alertes.pv');
      corpsSpecifique = (d, sinistre) => d('relance.corps.pv', { sinistre });
      break;
    }
    case 'RESPONSABILITE': {
      libelleCible = t('ecran.relance.cibles.assureurAvocat');
      if (sinistreData && (!sinistreData.positionRc || sinistreData.positionRc === 'EN_ATTENTE')) {
        manquants.push((d) => d('relance.manquants.positionRc'));
      }
      alerteContexte = t('ecran.relance.alertes.responsabilite');
      corpsSpecifique = (d, sinistre) => d('relance.corps.responsabilite', { sinistre });
      break;
    }
    case 'EXPERTISE': {
      libelleCible = t('ecran.relance.cibles.expert');
      alerteContexte = t('ecran.relance.alertes.expertise');
      corpsSpecifique = (d, sinistre) => d('relance.corps.expertise', { sinistre });
      break;
    }
    case 'OFFRE': {
      libelleCible = n.victimeNomPrenom ? t('ecran.relance.cibles.victime', { nom: n.victimeNomPrenom }) : t('ecran.relance.cibles.victimeRepresentant');
      typeDestinataire = 'VICTIME';
      alerteContexte = t('ecran.relance.alertes.offre');
      corpsSpecifique = (d, sinistre) => d('relance.corps.offre', { sinistre });
      break;
    }
    case 'PAIEMENT': {
      libelleCible = t('ecran.relance.cibles.comptable');
      alerteContexte = t('ecran.relance.alertes.paiement');
      corpsSpecifique = (d, sinistre) => d('relance.corps.paiement', { sinistre });
      break;
    }
    case 'RECOURS': {
      if (n.homologueRaisonSociale) {
        libelleCible = n.homologueRaisonSociale + (n.homologueCodePays ? ` (${n.homologueCodePays})` : '');
        typeDestinataire = 'HOMOLOGUE';
      organismeDestinataire = n.homologueTrackingId;
      }
      alerteContexte = t('ecran.relance.alertes.recours');
      corpsSpecifique = (d, sinistre) => d('relance.corps.recours', { sinistre });
      break;
    }
    case 'PRESCRIPTION': {
      alerteContexte = t('ecran.relance.alertes.prescription');
      corpsSpecifique = (d, sinistre, date) => d('relance.corps.prescription', { sinistre, date });
      break;
    }
    default: break;
  }

  // Pièces victime manquantes (si statut ATTENTE_PIECES)
  let victime = null;
  if (n.victimeTrackingId) {
    try {
      const r = await axiosInstance.get(`/v1/victimes/${n.victimeTrackingId}`);
      const v = r.data?.data;
      victime = v;
      if (v?.statutVictime === 'ATTENTE_PIECES') {
        manquants.push((d) => d('relance.manquants.piecesVictime', { nom: n.victimeNomPrenom || '' }));
      }
    } catch { /* silencieux */ }
  }

  /* Construction du corps final, dans la langue du destinataire */
  const langue = n.categorie === 'OFFRE' && victime
    ? langueVictime(victime)
    : await langueDocument(organismeDestinataire);
  const d = tDocument(langue);
  const libelle = libelleDelai(n, langue);
  const dateEcheance = formatDateDocument(n.dateEcheance, langue);
  const sinistre = n.sinistreNumeroLocal || d('relance.leDossier');
  const niveauLabel = ['CRITIQUE', 'URGENT', 'ATTENTION'].includes(n.niveauAlerte)
    ? d(`relance.niveaux.${n.niveauAlerte}`) : '';
  const lignesManquants = manquants.length > 0
    ? `\n\n${d('relance.elementsEnAttente')}\n` + manquants.map(m => `  • ${m(d)}`).join('\n')
    : '';
  const refLegale = n.referenceJuridique ? `\n\n${d('relance.reference')} ${n.referenceJuridique}` : '';

  const corpsSuggere = `${d('relance.bonjour')}

${corpsSpecifique(d, sinistre, dateEcheance, libelle)}

${d('relance.delai')} ${libelle}
${d('relance.echeance')} ${dateEcheance}${niveauLabel ? `\n${d('relance.niveau')} ${niveauLabel}` : ''}${lignesManquants}${refLegale}

${d('relance.remerciements')}

${d('relance.cordialement')}
${d('relance.signature')}`;

  const sujetSuggere = `[BNCB-CB] ${niveauLabel ? niveauLabel + ' - ' : ''}${libelle} - ${sinistre}`;

  return {
    destinataireSuggere,
    typeDestinataire,
    libelleCible,
    alerteContexte,
    manquants,
    sujetSuggere,
    corpsSuggere,
  langueDocument: langue,
  };
}
